import React, {useState} from 'react';

import {useUserLookupById} from './useUserLookupById';
import {useResendVerificationEmail} from './useResendVerificationEmail';
import {AuditLogButton} from '~/features/AuditLog/AuditLogButton';
import {UserInfo} from '~/types';
import {
  ActiveEntitlement,
  InactiveEntitlement,
  LookupError,
  WebPurchases,
} from '~/components';
import {adminApi} from '~/store/rtkQuery';
import {formatDate} from '~/modules/utils';

export const UserLookup = () => {
  const [query, setQuery] = useState('');
  const [submittedQuery, setSubmittedQuery] = useState('');
  const {
    downloadingUserInfoErrorMsg,
    downloadingUserInfoStatus,
    userData,
    searchUserInfo,
  } = useUserLookupById({userId: query});
  const prefetchWebPurchases = adminApi.usePrefetch('webPurchasesLookup');

  const isSearching = downloadingUserInfoStatus === 'requesting';

  return (
    <div className="p-5">
      <h1 className="text-2xl font-semibold">User Lookup</h1>
      <p className="mt-2 text-sm text-gray-600">
        Look up a user by email or userId to see their profile, entitlements,
        and usage.
      </p>

      <div className="mt-4 form-control max-w-md">
        <label className="label" htmlFor="user-query">
          <span className="label-text font-medium">User's email or userId</span>
        </label>
        <input
          id="user-query"
          className="input input-bordered w-full"
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value.trim())}
        />
        <button
          className={`btn mt-2 ${
            query.trim() && !isSearching ? 'btn-primary' : 'btn-outline'
          }`}
          onClick={() => {
            setSubmittedQuery(query);
            prefetchWebPurchases({query});
            searchUserInfo();
          }}
          disabled={!query.trim() || isSearching}>
          {isSearching ? 'Searching...' : 'Search'}
        </button>
      </div>

      {downloadingUserInfoStatus === 'error' && (
        <LookupError
          message={downloadingUserInfoErrorMsg}
          query={submittedQuery}
        />
      )}

      {downloadingUserInfoStatus === 'error' && submittedQuery && (
        <div className="mt-4">
          <WebPurchases query={submittedQuery} />
        </div>
      )}

      {userData && (
        <UserDetailView
          userData={userData}
          webPurchasesQuery={submittedQuery}
        />
      )}
    </div>
  );
};

const UserDetailView = ({
  userData,
  webPurchasesQuery,
}: {
  userData: UserInfo;
  webPurchasesQuery: string;
}) => {
  const {resendState, resendVerificationEmail} = useResendVerificationEmail();

  return (
    <div className="mt-6">
      <div className="flex flex-col gap-3">
        <div className="card outline outline-zinc-200 outline-1">
          <div className="card-body">
            <div className="flex items-center gap-2">
              <h2 className="card-title">Profile</h2>
              <span
                className={`badge badge-sm ${
                  userData.isParent ? 'badge-info' : 'badge-warning'
                }`}>
                {userData.isParent ? 'Parent' : 'Teen'}
              </span>
            </div>
            <p className="font-medium">
              {userData.profile?.name || userData.email}
            </p>
            <p className="text-sm text-gray-600">
              <span className="font-bold">userId:</span> {userData.userId}
            </p>
            <p className="text-sm text-gray-600">
              <span className="font-bold">email:</span> {userData.email}
            </p>
            <p className="text-sm text-gray-600">
              <span className="font-bold">Email verified:</span>{' '}
              {userData.emailVerified ? 'Yes' : 'No'}
            </p>

            {!userData.emailVerified && (
              <div className="mt-2">
                <button
                  className="btn btn-sm btn-primary"
                  onClick={() => resendVerificationEmail(userData.userId)}
                  disabled={resendState.status === 'submitting'}>
                  {resendState.status === 'submitting'
                    ? 'Sending...'
                    : 'Resend verification email'}
                </button>

                {resendState.status === 'success' && (
                  <div
                    role="alert"
                    className="alert alert-success alert-sm mt-2 max-w-md">
                    <span>Verification email sent.</span>
                  </div>
                )}
                {resendState.status === 'error' && (
                  <div
                    role="alert"
                    className="alert alert-error alert-sm mt-2 max-w-md">
                    <span>{resendState.message}</span>
                  </div>
                )}
              </div>
            )}

            <p className="text-sm text-gray-600">
              <span className="font-bold">Created:</span>{' '}
              {formatDate(userData.created)}
            </p>
            <p className="text-sm text-gray-600">
              <span className="font-bold">Active in the last week?</span>{' '}
              {userData.activeInLastWeek ? 'Yes' : 'No'}
            </p>
            <p className="text-sm text-gray-600">
              <span className="font-bold">DoB:</span>{' '}
              {userData.profile?.dateOfBirth ?? '-'}
            </p>
            <p className="text-sm text-gray-600">
              <span className="font-bold">Points total:</span>{' '}
              {userData.lunaClubPoints}
            </p>
            <div className="align-top">
              <AuditLogButton
                endpoints={['POST /users/resendVerificationEmail']}
              />
            </div>
          </div>
        </div>

        <div className="card outline outline-zinc-200 outline-1">
          <div className="card-body">
            <h2 className="card-title">Interested categories</h2>
            {userData.profile?.interestedCategories &&
            userData.profile.interestedCategories.length > 0 ? (
              <p className="text-sm text-gray-600">
                {userData.profile.interestedCategories.join(', ')}
              </p>
            ) : (
              <p className="text-sm text-gray-600">
                No interested categories set.
              </p>
            )}
          </div>
        </div>

        <div className="card outline outline-zinc-200 outline-1">
          <div className="card-body">
            <h2 className="card-title">Entitlements</h2>
            {userData.entitlementsDetail.length === 0 ? (
              <p className="text-sm text-gray-600">No active entitlements.</p>
            ) : (
              <div className="space-y-3">
                {userData.entitlementsDetail.map(entitlement => (
                  <ActiveEntitlement
                    key={entitlement.entitlement}
                    entitlement={entitlement}
                  />
                ))}
              </div>
            )}
            {userData.lastInactiveEntitlement && (
              <InactiveEntitlement
                entitlement={userData.lastInactiveEntitlement}
              />
            )}
          </div>
        </div>

        <WebPurchases query={webPurchasesQuery} hideWhenEmpty />

        <div className="card outline outline-zinc-200 outline-1">
          <div className="card-body">
            <h2 className="card-title">Period data</h2>
            {!userData.periodData ? (
              <p className="text-sm text-gray-600">No period data available.</p>
            ) : (
              <>
                <p className="text-sm text-gray-600">
                  <span className="font-bold">Number of periods:</span>{' '}
                  {userData.periodData.numberOfPeriods}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-bold">Last completed period:</span>{' '}
                  {userData.periodData.lastCompletedPeriodStartDate ?? '-'}
                  {' → '}
                  {userData.periodData.lastCompletedPeriodEndDate ?? '-'}
                </p>

                {userData.periodData.cycleStats && (
                  <div className="mt-2">
                    <p className="font-medium text-sm">Cycle stats</p>
                    <p className="text-sm text-gray-600">
                      <span className="font-bold">Average cycle length:</span>{' '}
                      {userData.periodData.cycleStats.averageCycleLength}
                    </p>
                    <p className="text-sm text-gray-600">
                      <span className="font-bold">Average period length:</span>{' '}
                      {userData.periodData.cycleStats.averagePeriodLength}
                    </p>
                    <p className="text-sm text-gray-600">
                      <span className="font-bold">Cycle length range:</span>{' '}
                      {userData.periodData.cycleStats.minCycleLength}–
                      {userData.periodData.cycleStats.maxCycleLength}
                    </p>
                    <p className="text-sm text-gray-600">
                      <span className="font-bold">Bleed length range:</span>{' '}
                      {userData.periodData.cycleStats.minBleedLength}–
                      {userData.periodData.cycleStats.maxBleedLength}
                    </p>
                    <p className="text-sm text-gray-600">
                      <span className="font-bold">Average bleed pattern:</span>{' '}
                      {userData.periodData.cycleStats.averageBleedPattern.join(
                        ', ',
                      ) || '-'}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
